/**
 * De monitoring (22 oktober, docs/beheer.md): waar bezoekers op tikken.
 *
 * Wat telt, staat in de acties van de kiosk-store (`state/store.ts`): een
 * tegel, terug, een keuze in een onderdeel, de film, de 3D-viewer, de taal, de
 * calculator. Op die plek en niet op de knoppen zelf, zodat een tik op een
 * hotspot in de hal net zo telt als op de knop eronder, en de telling niet
 * afhangt van de taal van de knoptekst.
 *
 * Anoniem: geen naam, geen gezicht, alleen het doel, de tijd en een
 * willekeurige sessiecode. De gebeurtenissen wachten in localStorage en gaan
 * elke halve minuut naar de Worker; zonder netwerk wachten ze tot er weer
 * verbinding is. De sessie-reset wist deze wachtrij niet.
 */

const WACHTRIJ = 'motrac-meting'
const APPARAAT = 'motrac-apparaat'
const BEHEER = 'motrac-kiosk-beheer'
/** Meer dan dit bewaren heeft geen zin: dan is de kiosk dagen offline geweest. */
const MAX_WACHTRIJ = 5000
const PER_KEER = 500
const ELKE = 30_000

interface Gebeurtenis {
  t: number
  sessie: string
  doel: string
  duur?: number
}

const toeval = () => Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)

function lees<T>(sleutel: string, anders: T): T {
  try {
    const ruw = localStorage.getItem(sleutel)
    return ruw === null ? anders : (JSON.parse(ruw) as T)
  } catch {
    return anders
  }
}

function schrijf(sleutel: string, waarde: unknown) {
  try {
    localStorage.setItem(sleutel, JSON.stringify(waarde))
  } catch {
    // Geen opslag: dan telt deze kiosk niet mee, maar hij draait gewoon.
  }
}

/** Een vaste, willekeurige code per apparaat: zo schrijft elke kiosk zijn eigen totaal. */
function apparaat(): string {
  let id = lees<string | null>(APPARAAT, null)
  if (!id || !/^[a-z0-9]{8,40}$/.test(id)) {
    id = toeval()
    schrijf(APPARAAT, id)
  }
  return id
}

/** De naam van deze kiosk ("1", "2", "3"), te kiezen in het beheer. */
export function kioskNaam(): string {
  return lees<{ kiosk?: string }>(BEHEER, {}).kiosk ?? ''
}

export function zetKioskNaam(naam: string) {
  schrijf(BEHEER, { ...lees<Record<string, unknown>>(BEHEER, {}), kiosk: naam })
}

let aan = false
let sessie: { id: string; begin: number; laatst: number } | null = null
let bezig = false

function voegToe(g: Gebeurtenis) {
  const rij = lees<Gebeurtenis[]>(WACHTRIJ, [])
  rij.push(g)
  schrijf(WACHTRIJ, rij.slice(-MAX_WACHTRIJ))
}

/**
 * Eén tik op `doel`. De eerste tik na een reset begint een sessie. Doet
 * niets buiten de kiosk (showroom, productpagina) en niet onder een
 * testrobot: de afdrukscripts tikken zich anders in de cijfers.
 */
export function meet(doel: string) {
  if (!aan || navigator.webdriver) return
  const nu = Date.now()
  if (!sessie) {
    sessie = { id: toeval(), begin: nu, laatst: nu }
    voegToe({ t: nu, sessie: sessie.id, doel: 'sessie:start' })
  }
  sessie.laatst = nu
  voegToe({ t: nu, sessie: sessie.id, doel })
}

/** De sessie-reset: de bezoeker is weg. De duur loopt van de eerste tot de laatste tik. */
export function eindeSessie() {
  if (!sessie) return
  voegToe({ t: Date.now(), sessie: sessie.id, doel: 'sessie:einde', duur: Math.round((sessie.laatst - sessie.begin) / 1000) })
  sessie = null
  void verstuur()
}

/** Stuurt wat er wacht. Eén verzoek tegelijk: het totaal per apparaat mag niet door elkaar lopen. */
export async function verstuur() {
  if (bezig || !navigator.onLine) return
  const rij = lees<Gebeurtenis[]>(WACHTRIJ, [])
  if (rij.length === 0) return
  bezig = true
  try {
    const deel = rij.slice(0, PER_KEER)
    const antwoord = await fetch('/api/meting', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ apparaat: apparaat(), kiosk: kioskNaam(), gebeurtenissen: deel }),
    })
    // Alleen bij succes uit de wachtrij, en dan precies wat er verstuurd is:
    // intussen kan er achteraan iets bij zijn gekomen.
    if (antwoord.ok) schrijf(WACHTRIJ, lees<Gebeurtenis[]>(WACHTRIJ, []).slice(deel.length))
  } catch {
    // Geen verbinding: volgende keer.
  } finally {
    bezig = false
  }
}

/** Hoeveel tikken er nog op versturen wachten (voor het beheer). */
export function wachtend(): number {
  return lees<Gebeurtenis[]>(WACHTRIJ, []).length
}

/** Aan, alleen in de kiosk zelf (`main.tsx`). */
export function startMeting() {
  if (aan) return
  aan = true
  window.setInterval(() => void verstuur(), ELKE)
  window.addEventListener('online', () => void verstuur())
  void verstuur()
}
